import React, { useMemo, useState } from 'react';
import ActionButton from 'view/components/buttons/ActionButton';
import evaluationService from 'services/evaluationService';
import { useClassroomContext } from 'contexts/ClassroomContext';
import useApiCall from 'contexts/ApiCall';
import TeacherGradebookEntry from 'models/TeacherGradebookEntry';
import { useGradebook } from 'contexts/TeacherGradebookContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FinePrint from 'view/components/common/FinePrint';
import { faChartSimple, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import StudentSelector from '../StudentSelector';
import { EVALUATION_DISPLAY_NAMES } from 'utils/types';

import 'view/style/teacher/components/assignment/assignmentTypeForm.css';

interface EvaluationFormProps {
  evalScoring: EvalScoring;
  onClose: () => void;
}

export type EvalType = 'wida' | 'lexile';

export interface EvalScoring {
  type: EvalType;
  min: number;
  max: number;
  default: number;
  value: number;
  override: boolean;
}

export interface Range {
  min: number;
  max: number;
  default: number;
}

const EvaluationForm: React.FC<EvaluationFormProps> = ({
  evalScoring,
  onClose,
}) => {
  const [scoring, setScoring] = useState<EvalScoring>(evalScoring);
  const { gradebook, setGradebook } = useGradebook();
  const [selectedStudents, setSelectedStudents] = useState<string[]>(
    gradebook?.students.map((s) => s.username) ?? [],
  );
  const { classroom } = useClassroomContext();
  const makeApiCall = useApiCall();

  const handleUpdateSelectedStudents = (
    selectedStudents: TeacherGradebookEntry[],
  ) => {
    setSelectedStudents(selectedStudents.map((s) => s.username));
  };

  const handleCreateEvaluation = () => {
    makeApiCall(
      evaluationService.createEvaluation,
      scoring.type,
      scoring.override,
      scoring.value,
      classroom?.id,
      classroom?.district,
      selectedStudents,
    )
      .then((resp) => {
        alert('Evaluation created!');
        setGradebook((prev) =>
          prev?.addEvaluation(resp.evaluation, resp.evaluation_submissions),
        );
        onClose();
      })
      .catch((error) => alert(error));
  };

  const baselineRange = useMemo(() => {
    switch (evalScoring.type) {
      case 'wida':
        return {
          min: 0.0,
          max: 6.0,
          default: 2.0,
        };
      case 'lexile':
        return {
          min: 0,
          max: 2000,
          default: 500,
        };
      default:
        console.error('Invalid evaluation type!');
    }
  }, [evalScoring.type]);

  return (
    <div
      className="assignment-sidebar-content"
      style={{ width: 'min(600px, 95vw)', textAlign: 'left' }}
    >
      <h3>{EVALUATION_DISPLAY_NAMES[evalScoring.type]} Evaluation</h3>
      <div className="assignment-attribute-container">
        <div className="sidebar-text">
          <FontAwesomeIcon icon={faChartSimple} />
          <label className="label-normal">Baseline score</label>
          <input
            id="evaluation-baseline-input"
            type="number"
            style={{ minWidth: 'fit-content' }}
            defaultValue={baselineRange?.default}
            max={baselineRange?.min}
            min={baselineRange?.max}
            onChange={(event) => {
              setScoring(() => ({
                ...scoring,
                baseline: event.target.value,
              }));
            }}
          />
        </div>
        <FinePrint
          text="Note: The default level for your students going into the evaluation"
          type="danger"
        />
      </div>
      <div className="assignment-attribute-container">
        <div className="sidebar-text">
          <FontAwesomeIcon icon={faSyncAlt} />
          <label className="label-normal">Always use baseline</label>
          <input
            type="checkbox"
            id="override_baseline"
            checked={scoring?.override}
            onChange={(event) => {
              setScoring(() => ({
                ...scoring,
                override: event.target.checked,
              }));
            }}
          />
        </div>
        <FinePrint
          type="danger"
          text={`By default we will start the evaluation at the current ${evalScoring.type.toUpperCase()} level approximation that we have stored for the student. If you would like to start all evaluations at the baseline level instead, check this box`}
        />
      </div>
      <StudentSelector
        onUpdateSelectedStudents={handleUpdateSelectedStudents}
      />
      <ActionButton type="go" label="Assign" onClick={handleCreateEvaluation} />
    </div>
  );
};

export default EvaluationForm;
